<script>
import MomParticipantService from "@/services/MomParticipantService";
import UserService from "@/services/UserService";

export default {
    props: {
        momId: {},
        projectId: {}
    },
    computed: {
        availableUsers() {
            const usedIds = this.participants
                .filter(p => p.user_id)
                .map(p => parseInt(p.user_id));
            return this.users.filter(u => !usedIds.includes(parseInt(u.value)));
        }
    },
    data() {
        return {
            loading: true,
            saving: false,
            participants: [],
            users: [],
            isInternal: true,
            newParticipant: {
                user_id: null,
                name: null,
                email: null,
                present: 1
            }
        };
    },
    async created() {
        await Promise.all([this.fetchParticipants(), this.fetchUsers()]);
        this.loading = false;
    },
    methods: {
        async fetchParticipants() {
            try {
                const { data } = await MomParticipantService.GET({
                    project_mom_id: this.momId
                });
                this.participants = data.data;
            } catch (e) {
                console.log("fetchParticipants e", e);
            }
        },
        async fetchUsers() {
            try {
                const { data } = await UserService.dropdown();
                this.users = data.data;
            } catch (e) {
                console.log("fetchUsers e", e);
            }
        },
        participantName(p) {
            if (p.user_id && p.user) return p.user.name;
            return p.name || "---";
        },
        async togglePresent(p) {
            try {
                await MomParticipantService.PUT({
                    id: p.id,
                    present: p.present == 1 ? 0 : 1
                });
                await this.fetchParticipants();
            } catch (e) {
                console.log("togglePresent e", e);
            }
        },
        async remove(p) {
            try {
                await MomParticipantService.DELETE(p.id);
                await this.fetchParticipants();
            } catch (e) {
                console.log("remove e", e);
            }
        },
        switchType(internal) {
            this.isInternal = internal;
            this.newParticipant = {
                user_id: null,
                name: null,
                email: null,
                present: 1
            };
        },
        async addParticipant() {
            try {
                this.saving = true;
                const payload = {
                    project_mom_id: this.momId,
                    project_id: this.projectId,
                    present: this.newParticipant.present ? 1 : 0
                };
                if (this.isInternal) {
                    payload.user_id = this.newParticipant.user_id;
                } else {
                    payload.name = this.newParticipant.name;
                    payload.email = this.newParticipant.email;
                }
                await MomParticipantService.POST(payload);
                this.newParticipant = {
                    user_id: null,
                    name: null,
                    email: null,
                    present: 1
                };
                await this.fetchParticipants();
            } catch (e) {
                console.log("addParticipant e", e);
            }
            this.saving = false;
        }
    }
};
</script>

<template>
    <modal
        size="xxl"
        :show="true"
        @close="$emit('close')"
        class="budget-modal"
        :loading="loading"
    >
        <template slot="header">{{ $t("moms.participants_title") }}</template>

        <template slot="default">
            <v-table class="table table-mg table-sm">
                <v-thead>
                    <v-tr>
                        <v-th>{{ $t("moms.participants_field_name") }}</v-th>
                        <v-th>{{ $t("moms.participants_field_email") }}</v-th>
                        <v-th class="text-center">
                            {{ $t("moms.participants_present") }}
                        </v-th>
                        <v-th></v-th>
                    </v-tr>
                </v-thead>
                <v-tbody>
                    <v-tr v-if="!participants.length">
                        <v-td colspan="4" class="text-center text-muted">
                            {{ $t("moms.no_participants") }}
                        </v-td>
                    </v-tr>
                    <v-tr v-for="p in participants" :key="p.id">
                        <v-td>
                            {{ participantName(p) }}
                            <i
                                v-if="!p.user_id"
                                class="far fa-external-link-alt fa-fw text-muted ml-1"
                            ></i>
                        </v-td>
                        <v-td>{{
                            p.email || (p.user && p.user.email) || "---"
                        }}</v-td>
                        <v-td class="text-center">
                            <i
                                class="far fa-lg fa-fw m-click"
                                :class="
                                    p.present == 1
                                        ? 'fa-check-circle text-success'
                                        : 'fa-circle text-muted'
                                "
                                @click="togglePresent(p)"
                            ></i>
                        </v-td>
                        <v-td class="text-right">
                            <i
                                class="far fa-trash fa-lg m-click text-danger"
                                @click="remove(p)"
                            ></i>
                        </v-td>
                    </v-tr>
                </v-tbody>
            </v-table>

            <hr />

            <h5>{{ $t("moms.participants_add") }}</h5>

            <div class="mb-3">
                <base-button
                    size="sm"
                    :type="isInternal ? 'primary' : 'secondary'"
                    @click="switchType(true)"
                >
                    {{ $t("moms.participants_internal") }}
                </base-button>
                <base-button
                    size="sm"
                    :type="!isInternal ? 'primary' : 'secondary'"
                    @click="switchType(false)"
                >
                    {{ $t("moms.participants_external") }}
                </base-button>
            </div>

            <FormulateForm v-model="newParticipant" @submit="addParticipant">
                <div class="row">
                    <div class="col-12" v-show="isInternal">
                        <FormulateInput
                            type="multiselect"
                            name="user_id"
                            :options="availableUsers"
                            :label="$t('moms.participants_field_user')"
                            :placeholder="$t('moms.participants_field_user')"
                        />
                    </div>
                    <div class="col-6" v-show="!isInternal">
                        <FormulateInput
                            type="text"
                            name="name"
                            :label="$t('moms.participants_field_name')"
                            :validation="!isInternal ? 'required' : ''"
                            :validation-name="
                                $t('moms.participants_field_name')
                            "
                        />
                    </div>
                    <div class="col-6" v-show="!isInternal">
                        <FormulateInput
                            type="text"
                            name="email"
                            :label="$t('moms.participants_field_email')"
                            :validation="!isInternal ? 'required' : ''"
                            :validation-name="
                                $t('moms.participants_field_email')
                            "
                        />
                    </div>
                    <div class="col-12">
                        <FormulateInput
                            type="fswitch"
                            name="present"
                            :label="$t('moms.participants_field_present')"
                        />
                    </div>
                </div>
                <div class="row mt-2">
                    <div class="col text-right">
                        <base-button
                            type="secondary"
                            :outline="true"
                            size="sm"
                            @click="$emit('close')"
                            >{{ $t("cancel") }}</base-button
                        >
                        <base-button
                            type="primary"
                            size="sm"
                            :loading="saving"
                            :disabled="
                                isInternal
                                    ? !newParticipant.user_id
                                    : !newParticipant.name ||
                                      !newParticipant.email
                            "
                            native-type="submit"
                        >
                            <i class="far fa-plus"></i>
                            {{ $t("moms.participants_add") }}
                        </base-button>
                    </div>
                </div>
            </FormulateForm>
        </template>
    </modal>
</template>
